import React, { useState, useMemo } from 'react';
import styles from './SalesReport.module.css';
import { API_BASE_URL } from "../Config.js";

const FIELD_OPTIONS = [
  { value: 'itemType', label: 'Item Type' },
  { value: 'description', label: 'Item Category' },
  { value: 'itemSize', label: 'Size' },
  { value: 'itemColor', label: 'Item Color' },
];

const SalesReport = ({ data }) => {
  const [billTypeFilter, setBillTypeFilter] = useState('RETAIL');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [draggedSourceField, setDraggedSourceField] = useState(null);

  // Stack: priority (first = highest)
  const [stack, setStack] = useState([]);

  // Global Asc / Desc
  const [globalOrder, setGlobalOrder] = useState("asc");

  // unique ID generator
  const nextId = (() => {
    let n = 1;
    return () => `lvl_${n++}`;
  })();

  // Add a new level to stack from available fields
  const addLevelFromField = (field) => {
    if (!field) return;
    if (stack.some(s => s.field === field)) return;

    const id = nextId();
    setStack(prev => [...prev, { id, field }]);
  };

  // Remove a level
  const removeLevel = (id) => {
    setStack(prev => prev.filter(s => s.id !== id));
  };

  // Drag handlers for stack reordering
  const handleStackDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleStackDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    e.dataTransfer.dropEffect = 'move';
    
    const newStack = [...stack];
    const draggedItem = newStack[draggedIndex];
    newStack.splice(draggedIndex, 1);
    newStack.splice(index, 0, draggedItem);
    
    setStack(newStack);
    setDraggedIndex(index);
  };

  const handleStackDrop = (e, dropIndex) => {
    e.preventDefault();
    setDraggedIndex(null);
  };

  const handleStackDragEnd = () => {
    setDraggedIndex(null);
  };

  // Drag handlers for available fields
  const handleFieldDragStart = (e, field) => {
    setDraggedSourceField(field);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleFieldDragEnd = () => {
    setDraggedSourceField(null);
  };

  // Drop zone for stack area
  const handleStackAreaDragOver = (e) => {
    e.preventDefault();
    if (draggedSourceField) {
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleStackAreaDrop = (e) => {
    e.preventDefault();
    if (draggedSourceField) {
      addLevelFromField(draggedSourceField);
      setDraggedSourceField(null);
    }
  };

  // Filter existing logic
  const filteredData = Array.isArray(data)
    ? data.filter(item => item.totalQuantity > 0 && (billTypeFilter === 'Both' || item.billType === billTypeFilter))
    : [];

  // Sorting logic using stack priority + global asc/desc
  const sortedData = useMemo(() => {
    if (stack.length === 0) return [...filteredData];

    return [...filteredData].sort((a, b) => {
      for (const level of stack) {
        const field = level.field;

        const va = a[field] ?? "";
        const vb = b[field] ?? "";

        const sa = typeof va === "string" ? va.toLowerCase() : va;
        const sb = typeof vb === "string" ? vb.toLowerCase() : vb;

        let cmp = 0;
        if (sa < sb) cmp = -1;
        else if (sa > sb) cmp = 1;

        if (cmp !== 0) {
          return globalOrder === "asc" ? cmp : -cmp;
        }
      }
      return 0;
    });
  }, [filteredData, stack, globalOrder]);

  const totalAmountSum = sortedData.reduce((sum, item) => sum + (Number(item.totalAmount) || 0), 0);

  // Export
  const handleExport = async () => {
    if (sortedData.length === 0) {
      alert('No data to export.');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/sales/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sortedData),
      });

      if (!response.ok) throw new Error('Failed to export sales report.');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Sales_Report.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  const availableOptions = FIELD_OPTIONS.filter(opt => !stack.some(s => s.field === opt.value));

  return (
    <div className={styles.salesReport}>

      {sortedData.length > 0 && (
        <div className={styles.exportButtonContainer}>
          <button className={styles.filterModalButton} onClick={() => setIsModalOpen(true)}>
            Filter & Sort
          </button>
          <button className={styles.salesExportButton} onClick={handleExport}>
            Export
          </button>
        </div>
      )}

      {/* MODAL */}
      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsModalOpen(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            
            <div className={styles.modalBody}>
              
              {/* Available Fields Section */}
              <div className={styles.fieldsSection}>
                <div className={styles.sectionTitle}>Available Fields (Click or Drag to add)</div>
                <div className={styles.fieldsContainer}>
                  {availableOptions.map(opt => (
                    <div
                      key={opt.value}
                      className={`${styles.fieldBox} ${draggedSourceField === opt.value ? styles.fieldDragging : ''}`}
                      draggable
                      onDragStart={(e) => handleFieldDragStart(e, opt.value)}
                      onDragEnd={handleFieldDragEnd}
                      onClick={() => addLevelFromField(opt.value)}
                    >
                      {opt.label}
                    </div>
                  ))}
                  {availableOptions.length === 0 && (
                    <div className={styles.emptyFields}>All fields added to sorting</div>
                  )}
                </div>
              </div>

              {/* Stack Section */}
              <div className={styles.stackSection}>
                <div className={styles.sectionTitle}>Sorting Priority</div>
                <div 
                  className={styles.stackContainerRow}
                  onDragOver={handleStackAreaDragOver}
                  onDrop={handleStackAreaDrop}
                >
                  {stack.length === 0 ? (
                    <div className={styles.emptyStateRow}>
                      Click or drag fields here to set sorting priority
                    </div>
                  ) : (
                    <>
                      {stack.map((s, i) => {
                        const label = FIELD_OPTIONS.find(f => f.value === s.field)?.label || s.field;
                        return (
                          <React.Fragment key={s.id}>
                            <div
                              className={`${styles.stackItemRow} ${draggedIndex === i ? styles.dragging : ''}`}
                              draggable
                              onDragStart={(e) => handleStackDragStart(e, i)}
                              onDragOver={(e) => handleStackDragOver(e, i)}
                              onDrop={(e) => handleStackDrop(e, i)}
                              onDragEnd={handleStackDragEnd}
                            >
                              <div className={styles.stackItemRowContent}>
                                <span className={styles.stackLabel}>{label}</span>
                                <button 
                                  className={styles.removeButtonRow}
                                  onClick={() => removeLevel(s.id)}
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                            {i < stack.length - 1 && (
                              <div className={styles.arrowIcon}>→</div>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>

              {/* Global Sorting Order */}
              <div className={styles.sortingOrderSection}>
                <div className={styles.sortingOrderLabel}>Sorting Order</div>
                <div className={styles.radioGroup}>
                  <label className={`${styles.radioOption} ${globalOrder === 'asc' ? styles.selected : ''}`}>
                    <input
                      type="radio"
                      value="asc"
                      checked={globalOrder === 'asc'}
                      onChange={(e) => setGlobalOrder(e.target.value)}
                    />
                    <span>Ascending</span>
                  </label>

                  <label className={`${styles.radioOption} ${globalOrder === 'desc' ? styles.selected : ''}`}>
                    <input
                      type="radio"
                      value="desc"
                      checked={globalOrder === 'desc'}
                      onChange={(e) => setGlobalOrder(e.target.value)}
                    />
                    <span>Descending</span>
                  </label>
                </div>
              </div>

              {/* Apply Button */}
              <button className={styles.applyButton} onClick={() => setIsModalOpen(false)}>
                Apply
              </button>
            </div>

          </div>
        </div>
      )}

      {/* TABLE */}
      <div className={styles.tableContainer}>
        {sortedData.length > 0 ? (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Bill Type</th>
                <th>Description</th>
                <th>Item Type</th>
                <th>Item Color</th>
                <th>Size</th>
                <th>Price</th>
                <th>Avg Sell Price</th>
                <th>Quantity</th>
                <th>Amount</th>
                <th>Item Desc</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((item, index) => (
                <tr key={index}>
                  <td>{item.billType}</td>
                  <td>{item.description}</td>
                  <td>{item.itemType}</td>
                  <td>{item.itemColor}</td>
                  <td>{item.itemSize}</td>
                  <td>{item.price}</td>
                  <td>{item.sellPrice}</td>
                  <td>{item.totalQuantity}</td>
                  <td>{item.totalAmount}</td>
                  <td>{item.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>No data available.</p>
        )}
      </div>

      <div className={styles.totalAmountSum}>
        <strong>Total Amount : </strong> {totalAmountSum}
      </div>

    </div>
  );
};

export default SalesReport;